/**
 * 
 * TODO: I am truly unsure what to do this with. At the same that I'd like these classes to be moved
 * to the model package, they would not be used elsewhere (outside of /ui)
 * 
 */

import { v4 as uuidv4 } from "uuid";
import { ExerciseData, ExerciseType, createNewExercise, FillInBlankExerciseType, SelectForBlankExerciseType } from "@org/model";
import { SortableItemData } from "../components/custom/expandable-sortable-item";

// Content types
export enum ContentType {
  Text = "Text",
  Image = "Image",
  Video = "Video",
  Exercise = "Exercise",
  Audio = "Audio",
  Document = "Document"
}

// Content item interface
export interface ContentItem extends SortableItemData {
  id: string;
  type: ContentType;
  order: number;
  data: any; // Specific data depends on the content type
  deleted?: boolean;
}

// Text content
export interface TextContent extends ContentItem {
  type: ContentType.Text;
  data: {
    text: string;
    format: "plain" | "markdown" | "html";
  };
}

// Image content
export interface ImageContent extends ContentItem {
  type: ContentType.Image;
  data: {
    url: string;
    title?: string;
    description?: string;
  };
}

// Video content
export interface VideoContent extends ContentItem {
  type: ContentType.Video;
  data: {
    url: string;
    title?: string;
    description?: string;
  };
}

// Base exercise content
export interface ExerciseContent extends ContentItem {
  type: ContentType.Exercise;
  data: ExerciseData & {
    exerciseSubtype: ExerciseType;
    correction: string;
    exerciseId?: string;
  };
}

// Multiple Choice exercise
export interface MultipleChoiceOptionContent {
  optionId?: string;
  label: string;
  isCorrect: boolean;
  order?: number;
}

export interface MultipleChoiceExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.MultipleChoice;
    correction: string;
    question: string;
    options: MultipleChoiceOptionContent[];
  };
}

// Written (Long/Short Answer) exercise
export interface WrittenExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.LongAnswer | ExerciseType.ShortAnswer;
    correction: string;
    format: "plain" | "markdown" | "html";
    textType: "long" | "short";
  };
}

// Recorded exercise
export interface RecordedExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.Recorded;
    correction: string;
    maxDurationSec: number;
  };
}

// Conversational exercise - for real-time AI voice conversations
export interface ConversationalExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.Conversational;
    correction: string;
    theme: string;
    maxDurationSec: number;
    systemPrompt?: string;
    targetSkills?: string[];
  };
}

// Matching exercise - drag and drop to connect pairs
export interface MatchingPairContent {
  pairId?: string;
  promptLabel: string;
  optionLabel: string;
  order?: number;
}

export interface MatchingExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.Matching;
    correction: string;
    pairs: MatchingPairContent[];
  };
}

// Scramble exercise - unscramble words to form sentences
export interface ScrambleSentenceContent {
  sentenceId?: string;
  correctSentence: string;
  order?: number;
  correction?: string;
}

export interface ScrambleExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.Scramble;
    correction: string;
    sentences: ScrambleSentenceContent[];
  };
}

export interface FillInBlankExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.FillInBlank;
    correction: string;
    sentences: Array<{
      sentenceId?: string;
      sentence: string;
      order?: number;
      correction?: string;
      blanks: Array<{
        blankId?: string;
        position?: number;
        answer: string;
        correction?: string;
      }>;
    }>;
  };
}

export interface SelectForBlankExerciseContent extends ExerciseContent {
  data: ExerciseData & {
    exerciseSubtype: ExerciseType.SelectForBlank;
    correction: string;
    sentences: Array<{
      sentenceId?: string;
      sentence: string;
      order?: number;
      correction?: string;
      blanks: Array<{
        blankId?: string;
        position?: number;
        options: Array<{
          optionId?: string;
          label: string;
          isCorrect: boolean;
          order?: number;
        }>;
      }>;
    }>;
  };
}

// Audio content
export interface AudioContent extends ContentItem {
  type: ContentType.Audio;
  data: {
    url: string;
    title?: string;
    description?: string;
  };
}

// Document content
export interface DocumentContent extends ContentItem {
  type: ContentType.Document;
  data: {
    url: string;
    title?: string;
    description?: string;
  };
}

// Block interface
export interface Block extends SortableItemData {
  id: string;
  name: string;
  description?: string;
  order: number;
  contents: ContentItem[];
  deleted?: boolean;
}

// Create empty block function
export const createNewBlock = (order: number): Block => ({
  id: uuidv4(),
  name: `Block ${order + 1}`,
  description: "",
  order,
  contents: [],
  type: "block"
});

const createEmptyMultipleChoiceOption = (
  isCorrect: boolean,
  order: number,
): MultipleChoiceOptionContent => ({
  optionId: uuidv4(),
  label: "",
  isCorrect,
  order,
});

// Create content item function
export const createNewContent = (type: ContentType, order: number, exerciseSubtype?: ExerciseType): ContentItem => {
  const baseContent = {
    id: uuidv4(),
    name: `${type} Content`,
    type,
    order,
    data: {}
  };

  switch (type) {
    case ContentType.Text:
      return {
        ...baseContent,
        data: {
          text: "",
          format: "plain"
        }
      } as TextContent;
    case ContentType.Image:
      return {
        ...baseContent,
        data: {
          url: "",
          title: "",
          description: ""
        }
      } as ImageContent;
    case ContentType.Video:
      return {
        ...baseContent,
        data: {
          url: "",
          title: "",
          description: ""
        }
      } as VideoContent;
    case ContentType.Audio:
      return {
        ...baseContent,
        data: {
          url: "",
          title: "",
          description: ""
        }
      } as AudioContent;
    case ContentType.Document:
      return {
        ...baseContent,
        data: {
          url: "",
          title: "",
          description: ""
        }
      } as DocumentContent;
    case ContentType.Exercise:
      if (exerciseSubtype === ExerciseType.MultipleChoice) {
        const defaultOptions = [
          createEmptyMultipleChoiceOption(true, 0),
          createEmptyMultipleChoiceOption(false, 1),
        ];
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.MultipleChoice, order),
            exerciseSubtype: ExerciseType.MultipleChoice,
            correction: "",
            question: "",
            options: defaultOptions,
          }
        } as MultipleChoiceExerciseContent;
      } else if (exerciseSubtype === ExerciseType.LongAnswer) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Written, order),
            exerciseSubtype: ExerciseType.LongAnswer,
            correction: "",
            format: "plain",
            textType: "long"
          }
        } as WrittenExerciseContent;
      } else if (exerciseSubtype === ExerciseType.ShortAnswer) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Written, order),
            exerciseSubtype: ExerciseType.ShortAnswer,
            correction: "",
            format: "plain",
            textType: "short"
          }
        } as WrittenExerciseContent;
      } else if (exerciseSubtype === ExerciseType.Recorded) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Recorded, order),
            exerciseSubtype: ExerciseType.Recorded,
            correction: "",
            maxDurationSec: 60
          }
        } as RecordedExerciseContent;
      } else if (exerciseSubtype === ExerciseType.FillInBlank) {
        const baseExercise = createNewExercise(ExerciseType.FillInBlank, order) as FillInBlankExerciseType;
        return {
          ...baseContent,
          data: {
            ...baseExercise,
            exerciseSubtype: ExerciseType.FillInBlank,
            correction: "",
            sentences: baseExercise.sentences?.map(sentence => ({
              ...sentence,
              sentenceId: (sentence as any).sentenceId ?? (sentence as any).id ?? undefined
              ,
              blanks: (sentence as any).blanks?.map((blank: any, blankIndex: number) => ({
                ...blank,
                blankId: blank.blankId ?? blank.id ?? undefined,
                position: typeof blank.position === "number" ? blank.position : blankIndex,
                correction: blank.correction ?? ""
              })) ?? [
                {
                  blankId: undefined,
                  position: 0,
                  answer: "",
                  correction: ""
                }
              ]
            })) ?? [
              {
                sentenceId: undefined,
                sentence: "___",
                order: 0,
                correction: "",
                blanks: [
                  {
                    blankId: undefined,
                    position: 0,
                    answer: "",
                    correction: ""
                  }
                ]
              }
            ]
          }
        } as FillInBlankExerciseContent;
      } else if (exerciseSubtype === ExerciseType.SelectForBlank) {
        const baseExercise = createNewExercise(ExerciseType.SelectForBlank, order) as SelectForBlankExerciseType;
        return {
          ...baseContent,
          data: {
            ...baseExercise,
            exerciseSubtype: ExerciseType.SelectForBlank,
            correction: "",
            sentences: baseExercise.sentences?.map(sentence => ({
              ...sentence,
              sentenceId: (sentence as any).sentenceId ?? (sentence as any).id ?? undefined,
              blanks: sentence.blanks?.map(blank => ({
                ...blank,
                blankId: (blank as any).blankId ?? (blank as any).id ?? undefined,
                options: blank.options?.map(option => ({
                  ...option,
                  optionId: (option as any).optionId ?? (option as any).id ?? undefined,
                  isCorrect: option.isCorrect ?? false
                })) ?? [
                  { optionId: undefined, label: "", isCorrect: true, order: 0 },
                  { optionId: undefined, label: "", isCorrect: false, order: 1 }
                ]
              })) ?? [
                {
                  blankId: undefined,
                  position: 0,
                  prompt: "",
                  options: [
                    { optionId: undefined, label: "", isCorrect: true, order: 0 },
                    { optionId: undefined, label: "", isCorrect: false, order: 1 }
                  ]
                }
              ]
            })) ?? [
              {
                sentenceId: undefined,
                sentence: "___",
                order: 0,
                correction: "",
                blanks: [
                  {
                    blankId: undefined,
                    position: 0,
                    options: [
                      { optionId: undefined, label: "", isCorrect: true, order: 0 },
                      { optionId: undefined, label: "", isCorrect: false, order: 1 }
                    ]
                  }
                ]
              }
            ]
          }
        } as SelectForBlankExerciseContent;
      } else if (exerciseSubtype === ExerciseType.Conversational) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Conversational, order),
            exerciseSubtype: ExerciseType.Conversational,
            correction: "",
            theme: "",
            maxDurationSec: 300,
            systemPrompt: "",
            targetSkills: []
          }
        } as ConversationalExerciseContent;
      } else if (exerciseSubtype === ExerciseType.Matching) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Matching, order),
            exerciseSubtype: ExerciseType.Matching,
            correction: "",
            pairs: [
              { pairId: undefined, promptLabel: "", optionLabel: "", order: 0 },
              { pairId: undefined, promptLabel: "", optionLabel: "", order: 1 }
            ]
          }
        } as MatchingExerciseContent;
      } else if (exerciseSubtype === ExerciseType.Scramble) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.Scramble, order),
            exerciseSubtype: ExerciseType.Scramble,
            correction: "",
            sentences: [
              { sentenceId: undefined, correctSentence: "", order: 0, correction: "" }
            ]
          }
        } as ScrambleExerciseContent;
      } else if (exerciseSubtype === ExerciseType.FileUpload) {
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.FileUpload, order),
            exerciseSubtype: ExerciseType.FileUpload,
            correction: "",
          }
        } as any; // FileUpload uses base exercise fields only
      } else {
        // Default to multiple choice if no subtype is specified
        const defaultOptions = [
          createEmptyMultipleChoiceOption(true, 0),
          createEmptyMultipleChoiceOption(false, 1),
        ];
        return {
          ...baseContent,
          data: {
            ...createNewExercise(ExerciseType.MultipleChoice, order),
            exerciseSubtype: ExerciseType.MultipleChoice,
            correction: "",
            question: "",
            options: defaultOptions,
          }
        } as MultipleChoiceExerciseContent;
      }
    default:
      return baseContent;
  }
};
